import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { drawPage } from '../../../render/renderer';
import { onResourcesChange } from '../../../render/resources';
import { ensureDocumentFonts, onFontsLoaded } from '../../../model/fonts';
import { invalidateTextLayouts } from '../../../model/textLayout';
import { pageOf } from '../../../model/operations';
import './styles.scss';

// A read-only rendering of one page, sized by its container: thumbnails in the gallery,
// the chat's art cards, the layer list. Same painter as the editor and the export.
function ArtPreview({ document: doc, pageId = null, className = '', label, rounded = true }) {
  const box = useRef(null);
  const canvas = useRef(null);
  const [width, setWidth] = useState(0);
  const [version, setVersion] = useState(0);
  useLayoutEffect(() => {
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(box.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => onResourcesChange(() => setVersion(value => value + 1)), []);
  useEffect(() => onFontsLoaded(() => { invalidateTextLayouts(); setVersion(value => value + 1); }), []);
  useEffect(() => {
    let alive = true;
    if (doc) ensureDocumentFonts(doc, pageId).then(() => alive && setVersion(value => value + 1));
    return () => { alive = false; };
  }, [doc, pageId]);
  useLayoutEffect(() => {
    const element = canvas.current;
    if (!element || !doc || !width) return;
    const scale = (width / doc.width) * Math.min(2, window.devicePixelRatio || 1);
    element.width = Math.round(doc.width * scale);
    element.height = Math.round(doc.height * scale);
    const ctx = element.getContext('2d');
    if (ctx) drawPage(ctx, doc, pageOf(doc, pageId), { scale });
  }, [doc, pageId, width, version]);
  return <div ref={box} className={`art-preview ${rounded ? 'is-rounded' : ''} ${className}`} role="img" aria-label={label}
    style={{ aspectRatio: doc ? `${doc.width} / ${doc.height}` : '4 / 5' }}>
    <canvas ref={canvas} aria-hidden="true" />
  </div>;
}

export default memo(ArtPreview);
